import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { batchNoFormatValidator } from 'src/app/stocks/shared/validators';
import { ProductBatchApi } from '../../../../services/api-service/stocks/product-batch-api';
import { AvailableBatchDto } from '../../../../stocks/product-batch/models/product-batch';
import { EasyNotificationService } from '../../../../services/easy-notification.service';
import { BatchSelectResultDto } from './models/batch-select';

@Component({
  selector: 'app-stocks-batch-select',
  templateUrl: './batch-select.component.html',
  styleUrls: ['./batch-select.component.css']
})
export class BatchSelectComponent {

  /** 輸入框參考 (focus 時清空, blur 時還原顯示值) */
  @ViewChild('batchInput') batchInput: ElementRef<HTMLInputElement>;

  /** mat-autocomplete trigger (async 載入完成後手動重開面板用) */
  @ViewChild('batchInput', { read: MatAutocompleteTrigger }) batchAutoTrigger: MatAutocompleteTrigger;

  /** 輸入框 id (供 Grid Tab 導航 focusGridInput 定位, 如 'batch-' + uid) */
  @Input() inputId = '';

  /** 商品 ID (用於載入可用批號) */
  @Input() productId: number = null;

  /** 庫別 ID (用於載入可用批號) */
  @Input() storeRoomId: number = null;

  /** 是否允許即時新增批號 (入庫類設 true) */
  @Input() allowCreate = false;

  /** 是否禁用 */
  @Input() disabled = false;

  /** 是否排除已過期批號 (以 referenceDate/單據日期為準、未提供則今天, 無效期批號保留; 入庫類進貨設 true, 預設 false 維持原行為) */
  @Input() excludeExpired = false;

  /** 過期比較基準日 (單據日期 ChangeDate)；未提供/無效時 fallback 今天。搭配 excludeExpired 使用。 */
  @Input() referenceDate: Date | string = null;

  /** 目前批號文字 (由父元件控制顯示值) */
  @Input() displayValue = '';

  /** 是否提供「未指定」溢流選項 (出庫類退貨設 true, 預設 false 維持原行為) */
  @Input() allowUnassigned = false;

  /** 各批可用量上限 (batchId → 夾制後庫存量)；提供時下拉顯示此值取代原 CurrentQty。null = 不夾制 (預設) */
  @Input() batchCeiling: { [batchId: number]: number } = null;

  /** 父層預先取得的可用批清單 (出庫作業已依扣庫規則撈好、算過跨列上限的批)；
   *  提供時取代本元件自行呼叫 GetAvailableBatches，使下拉集合與 ceiling 一致。
   *  null = 自行呼叫 GetAvailableBatches (預設，維持原行為) */
  @Input() externalBatches: AvailableBatchDto[] = null;

  /** 未指定批(溢流赤字池)現有量；提供時「未指定」選項顯示此量(可能為負)。null = 不顯示量(預設，維持原行為) */
  @Input() unassignedQty: number = null;

  /** 同商品+庫別載入過即快取選項，refocus 不重撈 (避免面板「開→關→重開」閃爍)。
   *  預設 false 維持原行為 (每次 focus 重撈最新庫存量)；清單長存的頁面 (如庫存批號分配) 設 true。 */
  @Input() cacheOptions = false;

  /** 批號選擇/輸入結果 */
  @Output() batchSelected = new EventEmitter<BatchSelectResultDto>();

  /** Tab 鍵按下 (供父層 Grid Tab 導航; 輸入中的批號由 blur 流程 commit) */
  @Output() tabPressed = new EventEmitter<KeyboardEvent>();

  /** 可用批號清單 */
  options: AvailableBatchDto[] = [];

  /** 過濾後的選項清單 (快取屬性, 僅在 options/keyword 變更時重算; 避免模板每次 CD 都跑 filter) */
  _filteredOptions: AvailableBatchDto[] = [];

  /** 輸入框目前輸入的關鍵字 */
  keyword = '';

  /** 是否正在載入 */
  isLoading = false;

  /** 已完成載入的快取鍵 (productId|storeRoomId)，供 cacheOptions 判斷是否可略過重撈 */
  private loadedKey: string = null;

  /**
   * 剛完成選取/輸入確認的旗標。
   * 選取後 input 仍 focused, 若此時有 in-flight 的 loadOptions 稍後回來會誤觸 reopenPanelIfFocused
   * 把已關閉的面板又打開 (選取批號後下拉又跳出)。設此旗標讓那次重開被略過;
   * 由 onFocus (使用者主動再點欄位) 重置, 不在 reopen 內清除以免殘留判斷錯誤。
   */
  private justCommitted = false;

  constructor(
    private productBatchApi: ProductBatchApi,
    private notification: EasyNotificationService
  ) { }

  /**
   * 取得焦點：保留目前批號文字 (讓使用者直接修改)，載入可用批號並開啟下拉。
   * 不清空輸入；面板穩定顯示由 reopenPanelIfFocused (async 載入完成後重開) 保證。
   */
  onFocus(): void {
    // 使用者主動點欄位, 重置選取旗標 (允許這次載入完成後重開面板)
    this.justCommitted = false;
    // 不清空: 保留目前批號文字讓使用者修改; keyword 帶入目前值以利下拉過濾, 載入完成後仍會開下拉
    this.keyword = this.batchInput ? (this.batchInput.nativeElement.value || '') : (this.displayValue || '');
    this.recalculateFilteredOptions();
    this.loadOptions();
  }

  /**
   * 失焦：未選取時, 若有輸入文字則視為輸入確認 (既有→選既有, 否則→新批號), 不清空;
   * 無輸入才還原顯示。延遲讓選取事件先完成。
   */
  onBlur(): void {
    setTimeout(() => {
      // 已由選取/Enter commit → 還原顯示為已選值
      if (this.justCommitted) {
        this.restoreDisplay();
        return;
      }
      const typed = (this.keyword || '').trim();
      if (!typed) {
        this.restoreDisplay();
        return;
      }
      // 有輸入未 commit → 視為輸入確認 (不清空); onEnterNew 處理既有/新批號與 allowCreate
      this.onEnterNew(typed);
      // 若 onEnterNew 未 commit (不允許新增且非既有), 還原避免殘留無效輸入
      if (!this.justCommitted) {
        this.restoreDisplay();
      }
    }, 200);
  }

  /** 還原輸入框顯示為目前批號 (displayValue) */
  private restoreDisplay(): void {
    if (this.batchInput) {
      this.batchInput.nativeElement.value = this.displayValue || '';
    }
    this.keyword = '';
    this.recalculateFilteredOptions();
  }

  /** 取得焦點時載入可用批號 (依商品+庫別) */
  loadOptions(): void {
    // 父層已提供清單 (出庫作業預先撈好) → 直接用，不自行呼叫 GetAvailableBatches
    if (this.externalBatches != null) {
      this.options = this.externalBatches || [];
      this.recalculateFilteredOptions();
      this.reopenPanelIfFocused();
      return;
    }
    if (!this.productId || !this.storeRoomId) {
      this.options = [];
      this.recalculateFilteredOptions();
      return;
    }
    // 快取命中：選項已是穩定資料，面板由 mat-autocomplete 於 focus 時直接開啟，不經 async 關閉/重開 (不閃爍)
    const key = this.productId + '|' + this.storeRoomId;
    if (this.cacheOptions && this.loadedKey === key) {
      this.recalculateFilteredOptions();
      return;
    }
    this.isLoading = true;
    this.productBatchApi.GetAvailableBatches(this.productId, this.storeRoomId)
      .then((res: AvailableBatchDto[]) => {
        this.options = res || [];
        this.loadedKey = key;
        this.recalculateFilteredOptions();
        this.isLoading = false;
        this.reopenPanelIfFocused();
      })
      .catch(() => {
        this.options = [];
        this.recalculateFilteredOptions();
        this.isLoading = false;
        this.notification.showGetDataFailed('批號');
      });
  }

  /**
   * async 載入完成後重開面板。
   * focus 當下批號還在 async 載入, mat-autocomplete 面板會因當下無穩定選項被關閉、且不會自動重開,
   * 造成「點一下閃一下就消失」(必須再輸入才會出現)。載入完成後若輸入框仍為焦點, 手動重開面板。
   */
  private reopenPanelIfFocused(): void {
    // 剛選取/確認過就不重開 (避免「選取批號後下拉又跳出」); 旗標由 onFocus 重置
    if (this.justCommitted) {
      return;
    }
    if (!this.batchInput || document.activeElement !== this.batchInput.nativeElement) {
      return;
    }
    // 等本次 CD 把 mat-option render 完再開, 否則面板讀到空選項仍會立即關閉
    setTimeout(() => {
      if (this.batchAutoTrigger && this.batchInput
        && document.activeElement === this.batchInput.nativeElement) {
        this.batchAutoTrigger.openPanel();
      }
    });
  }

  /** 選擇「未指定」溢流 (出庫類) */
  onSelectUnassigned(): void {
    this.justCommitted = true;
    this.batchSelected.emit({
      ProductBatchId: 0,
      BatchNo: '未指定',
      ExpirationDate: null,
      CurrentQty: 0,
      IsNew: false,
      IsUnassigned: true
    });
    this.keyword = '';
    if (this.batchAutoTrigger) {
      this.batchAutoTrigger.closePanel();
    }
  }

  /** 下拉顯示的庫存量 (有 batchCeiling 時顯示夾制後可用量, 否則原現有量) */
  displayQty(o: AvailableBatchDto): number {
    if (this.batchCeiling && this.batchCeiling[o.ProductBatchId] !== undefined) {
      // 夾制後可用量可能因其他列超額占用而為負，顯示 clamp 到 0（不影響確認驗證）
      return Math.max(0, this.batchCeiling[o.ProductBatchId]);
    }
    return o.CurrentQty;
  }

  /** 選擇既有批號 (mat-autocomplete 選項點擊) */
  onSelectExisting(opt: AvailableBatchDto): void {
    this.justCommitted = true;
    this.batchSelected.emit({
      ProductBatchId: opt.ProductBatchId,
      BatchNo: opt.BatchNo,
      ExpirationDate: opt.ExpirationDate,
      CurrentQty: opt.CurrentQty,
      IsNew: false,
      IsStopBatch: opt.IsStopBatch === true
    });
    this.keyword = '';
    if (this.batchAutoTrigger) {
      this.batchAutoTrigger.closePanel();
    }
  }

  /** 按 Enter 確認輸入的批號 (入庫類允許新增不存在的批號) */
  onEnterNew(batchNo: string): void {
    if (!batchNo || !batchNo.trim()) { return; }
    const trimmed = batchNo.trim();
    // 「未指定」為未指定溢流批的保留顯示值，不可當成新批號建立。
    // (選未指定後 displayValue='未指定'，若 re-focus 未改動直接 blur 會走到這裡；
    //  否則會被誤 emit 成 IsNew 的新批號，導致存檔時跳出新增批號 dialog。)
    if (this.allowUnassigned && trimmed === '未指定') { return; }
    // 完全相符(忽略大小寫)才視為選既有; 否則一律當待建新批號
    const upper = trimmed.toUpperCase();
    const existed = this.options.find(o => (o.BatchNo || '').trim().toUpperCase() === upper);
    if (existed) {
      this.onSelectExisting(existed);
      return;
    }
    if (!this.allowCreate) { return; }
    this.justCommitted = true;
    // 待建新批號: 不在此建檔, 效期/製造日由儲存時的新增批號 dialog 補齊
    this.batchSelected.emit({
      ProductBatchId: 0,
      BatchNo: trimmed,
      ExpirationDate: null,
      CurrentQty: 0,
      IsNew: true
    });
    this.keyword = '';
    if (this.batchAutoTrigger) {
      this.batchAutoTrigger.closePanel();
    }
  }

  /** 輸入事件 — 自動轉大寫 (對齊批號明細維護) + 更新 keyword + 重算過濾後的選項 */
  onInput(value: string): void {
    const upper = (value || '').toUpperCase();
    if (this.batchInput && this.batchInput.nativeElement.value !== upper) {
      this.batchInput.nativeElement.value = upper;
    }
    this.keyword = upper;
    this.recalculateFilteredOptions();
  }

  /** 批號格式檢查 (供輸入框紅底; 對齊批號明細維護的格式驗證) */
  get hasFormatError(): boolean {
    const kw = (this.keyword || '').trim();
    if (!kw) { return false; }
    return !!batchNoFormatValidator({ value: kw } as AbstractControl);
  }

  /** 重算效期過濾後的選項清單 (只在 options/keyword 變更時呼叫, 結果存入快取屬性) */
  private recalculateFilteredOptions(): void {
    let list = this.options;
    // 排除已過期 (僅 excludeExpired 時; 以今天為準, 無效期批號保留)
    // 註: 只過濾「顯示用」清單, this.options 保留全量供 onEnterNew 比對, 避免輸入既有過期批號被誤判為新批號
    if (this.excludeExpired) {
      const refStr = this.getReferenceDateString();
      list = list.filter(o => !o.ExpirationDate || o.ExpirationDate.substring(0, 10) >= refStr);
    }
    // 關鍵字過濾
    const kw = this.keyword && this.keyword.trim() ? this.keyword.trim().toUpperCase() : '';
    if (kw) {
      list = list.filter(o => o.BatchNo && o.BatchNo.toUpperCase().indexOf(kw) !== -1);
    }
    this._filteredOptions = list;
  }

  /** 取得過期比較基準日字串 (yyyy-MM-dd)：優先單據日期 referenceDate, 未提供/無效時 fallback 今天 (本地日期, 避免 toISOString 時區位移) */
  private getReferenceDateString(): string {
    let base = new Date();
    if (this.referenceDate) {
      const parsed = new Date(this.referenceDate);
      if (!isNaN(parsed.getTime())) { base = parsed; }
    }
    const yyyy = base.getFullYear();
    const mm = String(base.getMonth() + 1).padStart(2, '0');
    const dd = String(base.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }
}
